<template>
  <div class="edit-checkout">
    <b-row>
      <b-col>
        <b-alert
          :show="!isOnboardingRedesign && alert.show"
          variant="warning"
        >
          {{ alert.message }}
        </b-alert>
      </b-col>
    </b-row>
    <AlertMessage v-if="isOnboardingRedesign && alert.show">
      {{ alert.message }}
    </AlertMessage>

    <b-row
      v-if="!onboardingPatient?.non_hrt"
      class="mb-4"
    >
      <b-col>
        <label
          class="sub-text"
          :class="{'tw-text-lg': isOnboardingRedesign}"
        > Preferred Treatment Type </label>
        <v-select
          v-model="selectedTreatment"
          class="custom-input-style treatment-type"
          :options="options"
          label="text"
          :reduce="(options) => options.value"
          :clearable="false"
          :searchable="false"
          placeholder="Select treatment type"
        >
          <template #open-indicator="{ attributes }">
            <b-icon
              v-bind="attributes"
              icon="chevron-down"
            />
          </template>
        </v-select>
      </b-col>
    </b-row>

    <b-row
      v-if="onboardingPatient?.non_hrt"
      class="mt-2"
    >
      <b-col v-if="editedPlan.length > 0">
        <ProductRevealCheckboxV2
          v-for="product in editedPlan"
          ref="ProductRevealCheckbox"
          :key="product.id"
          :product="product"
          :mini="isOnboardingRedesign ? true : false"
          page="summary"
          @set-selection="updateSelection"
          @update-shipping="updateShipping"
        />
      </b-col>
    </b-row>
    <b-row
      v-else
      class="mt-2"
    >
      <b-col v-if="editedPlan.length > 0">
        <ProductRevealCheckbox
          v-for="product in editedPlan"
          ref="ProductRevealCheckbox"
          :key="product.id"
          :product="product"
          :mini="isOnboardingRedesign ? true : false"
          @set-selection="updateSelection"
          @update-shipping="updateShipping"
        />
      </b-col>
    </b-row>
    <b-modal
      v-model="progesteroneModal"
      hide-header
      hide-footer
      centered
      :content-class="{'dark-bg': !isOnboardingRedesign}"
      no-close-on-backdrop
      backdrop.preventDefaultult
      no-close-on-esc
    >
      <div v-if="isOnboardingRedesign">
        <div class="tw-px-10 lg:tw-px-20 tw-mb-6">
          <h2 class="tw-text-2xl tw-mb-2">
            Because of your medical history, it would not be safe to take Estrogen
            without Progesterone.
          </h2>
        </div>
        <div
          class="tw-bg-white -tw-mx-6 -tw-mb-6 tw-px-6 tw-py-6 tw-rounded-b-[0.5rem]"
          style="box-shadow: -8px -6px 23.3px 0px rgba(45, 43, 43, 0.05);"
        >
          <div class="tw-max-w-[400px] tw-mx-auto">
            <b-button
              class="slight-rounded"
              pill
              block
              variant="info"
              :disabled="buttonDisabled"
              @click="progesteroneModal = false"
            >
              Continue
            </b-button>
          </div>
        </div>
      </div>
      <template v-else>
        <b-row class="mb-6">
          <b-col cols="12">
            <div class="icon-logo dark"></div>
          </b-col>
        </b-row>
        <div class="modal-card">
          <p
            class="sub-text"
            style="color: inherit"
          >
            Because of your medical history, it would not be safe to take Estrogen
            without Progesterone.
          </p>
          <b-button
            variant="info"
            pill
            block
            class="slight-rounded"
            @click="progesteroneModal = false"
          >
            Continue
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-row
      v-if="!isOnboardingRedesign"
      class="d-flex jutify-content-end tw-mb-20"
    >
      <b-col>
        <b-button
          variant="outline-info"
          pill
          size="lg"
          class="mr-2 mb-2 slight-rounded"
          :disabled="buttonDisabled"
          @click="$emit('close-edit')"
        >
          Cancel
        </b-button>
        <b-button
          variant="info"
          pill
          size="lg"
          class="mb-2 slight-rounded"
          :disabled="buttonDisabled"
          @click="submitAnswer"
        >
          Update
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { OnboardingService } from '@/services/onboarding.service';
import { LookUpService } from '@/services/lookup.service';
import ProductRevealCheckbox from '@/components/onboarding/treatments/ProductRevealCheckbox';
import ProductRevealCheckboxV2 from '@/components/onboarding/treatments/ProductRevealCheckboxV2';
import AlertMessage from '@/components/partials/AlertMessage.vue';

export default {
  components: {
    AlertMessage,
    ProductRevealCheckbox,
    ProductRevealCheckboxV2
  },
  props: {
    desiredPlan: {
      type: Array,
      default() {
        return [];
      },
    },
    productReveal: {
      type: Array,
      default() {
        return [];
      },
    },
    treatmentForm: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      alert: {
        show: false,
        message: '',
      },
      editedPlan: [],
      progesteroneModal: false,
      buttonDisabled: false,
      options: [],
      selectedTreatment: '',
      loaded: false,
      treatmentState: {}
    };
  },
  computed: {
    ...mapState('global', ['isOnboardingRedesign']),
    ...mapState('onboarding', ['onboardingPatient']),
    hasSelectedTreatment() {
      return Object.values(this.treatmentState).some(
        value => value
      );
    },
  },
  watch: {
    hasSelectedTreatment: {
      immediate: true,
      handler (value) {
        this.$emit('has-selected-treatment', value);
      },
    },
    productReveal: {
      handler: function () {
        this.editedPlan = this.productReveal;
      },
      deep: true,
    },
    async selectedTreatment() {
      if (this.loaded && this.selectedTreatment == 'nopreference') {
        this.selectedTreatment = 'cream';
        return;
      }
      if (this.loaded && this.selectedTreatment != '') {
        const { data } = await OnboardingService.getProductRevealView(
          this.selectedTreatment
        );
        this.editedPlan = data;
        const desiredPlanProductIds = this.desiredPlan.map(
          o => o.productid
        );
        this.treatmentState = {}
        this.treatmentState = this.editedPlan.reduce((cur, acc) => {
          if (desiredPlanProductIds.includes(acc.id)) {
            this.$set(cur, acc.id, true);
          } else {
            this.$set(cur, acc.id, false);
          }
          return cur;
        }, {});
      }
    },
    editedPlan() {
      if (!this.loaded && this.editedPlan.length > 0) {
        this.selectedTreatment = this.treatmentForm;

        this.$nextTick(() => {
          const self = this;
          for (const currentProduct of self.$refs.ProductRevealCheckbox) {
            const desiredProduct = self.desiredPlan.find(
              o => o.productid == currentProduct.product.id
            );
            
            if (desiredProduct) {
              currentProduct.setSelected(true);
              if (desiredProduct.pricingdetail && desiredProduct.pricingdetail.pricingid) {
                currentProduct.selectedpricing = desiredProduct.pricingdetail.pricingid;
              }
            } else {
              currentProduct.setSelected(false);
            }
          }
        });
        this.loaded = true;
      }
    },
  },
  mounted() {
    // Autoselection
    this.getOptions();
    this.editedPlan = this.productReveal;
  },
  methods: {
    showAlert(message) {
      this.alert.show = true;
      this.alert.message = message;
    },
    updateSelection(id, selected, pricing, productselect, productdeselect) {
      if (selected) {
        if (!this.editedPlan.some(obj => obj.productid != id)) {
          this.editedPlan.push({
            productid: id,
            pricingid: pricing,
          });
        } else {
          this.editedPlan.forEach(o => {
            if (o.productid == id && o.pricingid != pricing) {
              o.pricingid = pricing;
            }
          });
        }
      } else {
        this.editedPlan = this.editedPlan.filter(obj => obj.productid != id);
      }
      this.$set(this.treatmentState, id, selected);
      for (const currentProduct of this.$refs.ProductRevealCheckbox) {
        if (productselect != undefined) {
          if (productselect.includes(currentProduct.product.id) && selected) {
            currentProduct.setSelected(true);
            this.$set(this.treatmentState, currentProduct.product.id, true);
          }
        }

        if (productdeselect != undefined) {
          if (
            productdeselect.includes(currentProduct.product.id) &&
            !selected
          ) {
            if (currentProduct.selected) {
              this.progesteroneModal = true;
            }
            currentProduct.setSelected(false);
            this.$set(this.treatmentState, currentProduct.product.id, false);
          }
        }
      }
    },
    updateShipping(
      id,
      pricing,
      productselect,
      productdeselect,
      availablepricing
    ) {
      this.editedPlan.forEach(o => {
        if (o.productid == id && o.pricingid != pricing) {
          o.pricingid = pricing;
        }
      });

      const currentSubscriptionType = availablepricing.find(o => {
        return o.pricingid == pricing;
      }).subscriptiontype;

      if (productselect != undefined || productdeselect != undefined) {
        for (const currentProduct of this.$refs.ProductRevealCheckbox) {
          if (
            (productselect != undefined &&
              productselect.includes(currentProduct.product.id)) ||
            (productdeselect != undefined &&
              productdeselect.includes(currentProduct.product.id))
          ) {
            if (currentProduct.selected) {
              const targetPricings = currentProduct.product.availablepricing;
              currentProduct.selectedpricing = targetPricings.find(o => {
                return o.subscriptiontype == currentSubscriptionType;
              }).pricingid;
            }
          }
        }
      }
    },
    async submitAnswer() {
      this.alert.show = false;
      this.buttonDisabled = true;
      const updatedPlan = [];

      if (!this.onboardingPatient?.non_hrt) {
        if (!this.selectedTreatment) {
          this.showAlert('Please select at least one treatment form.');
          this.buttonDisabled = false;
          return true;
        }
  
        // Change treatmentform
        const updateTreatmentForm = await OnboardingService.updateTreatmentForm(
          {
            treatmentform: this.selectedTreatment,
          }
        );
      }

      for (const currentProduct of this.$refs.ProductRevealCheckbox) {
        if (currentProduct.selected) {
          updatedPlan.push({
            productid: currentProduct.product.id,
            pricingid: currentProduct.selectedpricing,
          });
        }
      }

      if (updatedPlan.length <= 0) {
        if (this.isOnboardingRedesign) {
          this.showAlert('Please select at least one of our treatment recommendations.');
        } else {
          this.showAlert('Please select at least one product.');
        }
        this.buttonDisabled = false;
        return true;
      }

      const params = {
        agreedtomedication: true,
        desiredplan: updatedPlan,
      };
      const updateOnboarding = await OnboardingService.updatePatientOnboarding(
        3.2,
        params
      );
      this.$emit('update-checkout');
      this.buttonDisabled = false;
      return false
    },
    async getOptions() {
      let { data } = await LookUpService.getLookUp('treatmentform');
      data = data.filter(x => x.othername2 != undefined);
      for (const item of data) {
        this.options.push({
          value: item.id,
          text: item.othername2,
        });
      }
    },
  },
};
</script>

<style></style>
